// "Find prospects" (steps 2.16-2.21): the shapes the prospects function and the prospect database return, and the
// call that turns a niche and a place into a search plan. The plan, the count and the estimate come from the server;
// approving, ticking and copying go through act (lib/crm.ts).
import { env, functionUrl } from "./env";
import type { AdviserKind } from "./emailSources";
import { fmtNumber, humanise } from "./format";
import { httpFetch } from "./http";

/** A firm in Ogma's prospect database (crm_ref_search). Business facts only: contact points are never returned. */
export interface RefFirm {
  id: string;
  name: string;
  country: string;
  region: string | null;
  city: string | null;
  legal_form: string | null;
  website: string | null;
  domain: string | null;
  size_band: string | null;
  status: string;
  niches: string[];
  socials: Record<string, string>;
  has_contact: boolean;
  /** The firm already in this business's CRM, if any. */
  in_crm: string | null;
  /** E64 What the firm does, from its own website (read by Jev): Ogma's sector and sub-sector, and its own words. */
  profile?: FirmProfile | null;
  /** 2.37 Held for manual outreach only: why, or null when automated outreach may reach it. */
  outreach_hold?: "not_a_company" | "country_not_cleared" | "set_by_hand" | "email_unproven" | null;
  /** 27 Oct 2026 How Find can reach the firm (ref.search_firms): A a verified personal email, B a general inbox, C a
   *  social profile, phone or form only, M manual outreach only. */
  grade?: ContactGrade | null;
  /** Timing signals: growing, hiring, new_director, dmarc_missing, weak_web. */
  why_now?: string[];
  /** Search v2 What the search table holds: staff filed or estimated (E75), web presence, the sales team, labels. */
  readings?: FirmReadings | null;
}

/** E77 An adviser firm's clients: by how it is named for them, then by the clients' keys (with "_all"). */
export type ClientCounts = Partial<Record<AdviserKind, Record<string, number>>>;

const CLIENT_VERBS: Record<AdviserKind, string> = {
  accountants: "Files accounts for", auditors: "Audits", company_secretary: "Company secretary for",
};

/** "Files accounts for 26 TICC firms": for each kind of adviser the firm is, its clients with the plan's keys (the
 *  largest count when several), or all its clients when the plan names none. Null when it is no one's adviser. */
export function clientsLabel(counts: ClientCounts | null | undefined, keys: string[], names: Map<string, string>): string | null {
  if (!counts) return null;
  const lines: string[] = [];
  for (const kind of Object.keys(CLIENT_VERBS) as AdviserKind[]) {
    const by = counts[kind];
    if (!by) continue;
    const best = keys.map((k) => [k, by[k] ?? 0] as const).sort((a, b) => b[1] - a[1])[0];
    if (best && best[1] > 0) {
      lines.push(`${CLIENT_VERBS[kind]} ${fmtNumber(best[1])} ${names.get(best[0]) ?? humanise(best[0])} ${best[1] === 1 ? "firm" : "firms"}`);
    } else if (!keys.length && by._all) {
      lines.push(`${CLIENT_VERBS[kind]} ${fmtNumber(by._all)} ${by._all === 1 ? "firm" : "firms"}`);
    }
  }
  return lines.length ? lines.join(" · ") : null;
}

export interface FirmReadings {
  employees?: number;
  employees_est?: number;
  turnover?: number;
  turnover_est?: number;
  web_score?: number;
  web_weak?: boolean;
  socials?: number;
  sales_team?: "yes" | "no" | "unknown";
  labels?: string[];
  core_labels?: string[];
  clients?: ClientCounts;
}

/** "12 staff", or "about 8 staff (estimated)" when none is filed; null when neither is known. */
export function staffLabel(r: FirmReadings | null | undefined): string | null {
  if (r?.employees != null) return `${r.employees} staff`;
  if (r?.employees_est != null) return `about ${r.employees_est} staff (estimated)`;
  return null;
}

export type ContactGrade = "A" | "B" | "C" | "M";
export const GRADE_LABELS: Record<ContactGrade, string> = {
  A: "Personal email", B: "General inbox", C: "Social or phone only", M: "By hand only",
};

export interface FirmProfile {
  sector?: string;
  sector_key?: string;
  sub_sector?: string;
  sub_sector_key?: string;
  confidence?: number;
  customer_type?: "b2b" | "b2c" | "both" | "public_sector";
  reach?: "local" | "regional" | "national" | "international";
  headline?: string;
  summary?: string;
  services?: string[];
  sells_online?: boolean;
  hiring?: boolean;
  has_sales_people?: boolean;
}

export const CUSTOMER_TYPE_LABELS: Record<string, string> = {
  b2b: "Sells to businesses", b2c: "Sells to consumers", both: "Businesses and consumers", public_sector: "Public sector",
};

/** 2.37 What the "Manual outreach only" tag says about why. */
export const OUTREACH_HOLD_LABELS: Record<string, string> = {
  not_a_company: "Not a registered company: automated outreach never reaches it; contact them yourself.",
  country_not_cleared: "This country is not cleared for automated outreach in your settings; contact them yourself.",
  set_by_hand: "Set to manual outreach by someone in your business.",
  email_unproven: "Their only address is at a domain that accepts any address, so Ogma can't prove it; send it yourself.",
};

export interface RefNiche {
  key: string;
  parent_key: string | null;
  labels: Record<string, string>;
  search_terms: Record<string, string[]>;
  firms: number;
  /** N-01 A sector or sub-sector of Ogma's model rather than a niche of the registry. */
  kind?: "niche" | "sector" | "sub_sector";
}

/** N-01 A sector or sub-sector of Ogma's model with its profiled firms (crm_ref_taxonomy_counts). */
export interface RefSectorCount {
  key: string;
  kind: "sector" | "sub_sector";
  parent_key: string | null;
  label: string;
  firms: number | string;
}

/** N-01 The registry's niches, then the sectors and sub-sectors (a key in both keeps the niche, with both counts). */
export function withSectors(niches: RefNiche[], sectors: RefSectorCount[]): RefNiche[] {
  const out: RefNiche[] = niches.map((n) => ({ ...n }));
  for (const s of sectors) {
    if (!s || typeof s.key !== "string") continue;
    const have = out.find((n) => n.key === s.key);
    if (have) { have.firms = Number(have.firms ?? 0) + Number(s.firms ?? 0); continue; }
    out.push({ key: s.key, parent_key: s.parent_key ?? null, labels: { en: s.label || s.key }, search_terms: {},
               firms: Number(s.firms ?? 0), kind: s.kind });
  }
  return out;
}

/** #540 A range of a firm fact; either end may be open. */
export interface FactRange {
  min?: number;
  max?: number;
}

/** #540 What the firm is like, from its filings (as supabase/functions/prospects/lib/plan.ts FirmFacts). */
export interface FirmFacts {
  employees?: FactRange;
  /** In pounds. */
  turnover?: FactRange;
  owner_led?: boolean;
  independent?: boolean;
  growth?: ("rising" | "steady")[];
  trading_years?: number;
}

export type FactKey = keyof FirmFacts;

/** crm_ref_search's firm-fact parameters; none without facts. */
export function factArgs(f: FirmFacts | undefined): Record<string, number | boolean | string[] | null> {
  if (!f) return {};
  return {
    p_employees_min: f.employees?.min ?? null, p_employees_max: f.employees?.max ?? null,
    p_turnover_min: f.turnover?.min ?? null, p_turnover_max: f.turnover?.max ?? null,
    p_owner_led: f.owner_led ?? null, p_independent: f.independent ?? null,
    p_growth: f.growth ?? null, p_trading_years_min: f.trading_years ?? null,
  };
}

export interface ProspectPlan {
  title: string;
  niches: string[];
  countries: string[];
  regions: string[];
  terms: Record<string, string[]>;
  size_bands: string[];
  exclude_terms: string[];
  /** 2.38 What makes a firm a good prospect for this request; present when the request asked for a ranked list. */
  rank?: RankPlan;
  /** The search this one was copied from to change its criteria (crm_copy_prospect_search). */
  copied_from?: string;
  /** N-01 A specialism no key captures ("R&D tax"): it narrowed the search within the keys when query_applied. */
  query_text?: string;
  query_applied?: boolean;
  /** #540 The firm facts the search filters on; with them, niches may be empty (any trade). */
  facts?: FirmFacts;
  /** Who the firms sell to, from their own websites. */
  customer_types?: string[];
  /** 27 Oct 2026 What Ogma guessed to make the request searchable, for the owner to check and change. */
  assumptions?: string;
  /** Search v2 (29 Oct 2026): every spelling of the specialism; labels from firms' own sites; online presence; sales
   *  team; the parts a firm must meet (the rest only rank); the request's main point; what the planner did. */
  words?: string[];
  labels?: string[];
  /** 30 Oct 2026 Specialists only: a firm counts only when its site leads with one of the labels. */
  labels_core?: boolean;
  /** E77 Advisers by their clients: the clients' keys, how the adviser is named for them, and the fewest clients. */
  clients?: { keys: string[]; kind?: AdviserKind; min?: number };
  web?: "limited" | "strong";
  sales_team?: "none" | "has";
  need?: Facet[];
  focus?: string;
  steps?: string[];
}

/** The parts of a plan, as `need` names them (as supabase/functions/prospects/lib/plan.ts FACETS). */
export type Facet = "niches" | "regions" | "words" | "labels" | "size_bands" | "employees" | "turnover" | "owner_led"
  | "independent" | "growth" | "trading_years" | "customer_types" | "web" | "sales_team" | "clients";

/** A plan as the prospects function takes it back to search again without Claude: its fields, the firm facts at the
 *  top level, and the criteria to rank by. */
export type PlanInput = Partial<Omit<ProspectPlan, "facts" | "rank">> & FirmFacts & { criteria?: Partial<RankCriterion>[]; limit?: number };

export interface RankCriterion {
  key: string;
  label: string;
  question: string;
  kind: "must" | "nice";
  weight: number;
  keywords?: string[];
}

export interface RankPlan {
  countries: string[];
  niches: string[];
  limit: number;
  criteria: RankCriterion[];
}

/** 2.38 One firm in a ranked search: the score, why it is on the list (with the sentence from its own site), and who
 *  to contact through which channel. Contact points (email, phone) stay out of the list. */
export interface RankedFirm {
  id: string;
  name: string;
  country: string | null;
  region: string | null;
  city: string | null;
  legal_form: string | null;
  website: string | null;
  domain: string | null;
  size_band: string | null;
  headline: string | null;
  outreach_hold: string | null;
  decision: "pending" | "tick" | "cross";
  reason: string | null;
  organisation_id: string | null;
  score: number | null;
  reasons: { key: string; label: string; p: number; evidence: string | null }[];
  failed: string[] | null;
  site_read: boolean | null;
  contact: { name?: string; title?: string; why?: string; channel?: string; instagram?: string } | null;
}

export interface RankedList {
  total: number;
  rows: RankedFirm[];
  ranked_at: string | null;
  stats: Record<string, number> | null;
}

/** The review list's three groups (crm_prospect_ranked_groups, migration 20261218090000), from each firm's answers to
 *  the must-haves: every one met; Ogma's niche check and at least one other met; the rest of the niche searched. */
export type RankedGroup = "all" | "some" | "rest";
export const RANKED_GROUPS: readonly RankedGroup[] = ["all", "some", "rest"];

export interface GroupedFirm extends RankedFirm {
  group: RankedGroup;
  /** Must-haves met, and how many there are, not counting Ogma's own niche check. */
  musts_met: number;
  musts_total: number;
}

export interface GroupedList {
  counts: Record<RankedGroup, number>;
  total: number;
  group: RankedGroup | null;
  offset: number;
  rows: GroupedFirm[];
  /** The must-haves' labels, not counting Ogma's own niche check. */
  musts: string[];
  ranked_at: string | null;
  stats: Record<string, number> | null;
}

/** A search's criteria can be changed until it is approved (crm_set_prospect_criteria); after that, copy it. */
export function criteriaEditable(s: Pick<ProspectSearch, "status" | "decided_by">): boolean {
  return (s.status === "draft" || s.status === "estimated") && !s.decided_by;
}

export interface ProspectEstimate {
  central: number;
  needs_live: boolean;
  live_searches: number;
  live_cost_usd: number;
  live_minutes: number;
  cap_usd: number;
  within_cap: boolean;
  /** #540 A broad plan with no region: the top regions, to ask where to look first. */
  where?: { region: string; firms: number }[];
  /** Fewer than 100 matched: the firms each need-to-have's removal would give, most first. */
  dropped?: { criterion: string; firms: number }[];
  /** Search v2 Every need-to-have with the firms relaxing it would give (up to 10,000), for its chip. */
  relax?: { criterion: string; firms: number }[];
  /** Search v2 Over 5,000 matched: the counts by sub-sector and by size, to narrow the search. */
  sub_sectors?: { sub_sector: string; firms: number }[];
  sizes?: { size_band: string; firms: number }[];
  /** Matches already in this business's CRM, left out of the list. */
  in_crm?: number;
  /** The firms kept, best first, and how many of them make the top-20 sample ranked at once. */
  recorded?: number;
  sample?: number;
}

/** 2.38 How far a search has got (crm_prospect_progress), for the live progress. */
export interface SearchProgress {
  status: ProspectSearch["status"];
  created_at: string;
  ranked_at: string | null;
  total: number;
  in_crm: number;
  recorded: number;
  sample: number;
  sample_ranked: number;
  ranked: number;
  to_rank: number;
  /** A campaign has been made from the search: only then is its list scored (31 Oct 2026). */
  campaign?: boolean;
  ranks: boolean;
  /** Scoring paused (migration 20270228090000): no credits (an hour on) or the daily cap (the next day); null when going. */
  blocked?: { kind: "credits" | "cap" | "month"; until: string } | null;
}

export interface PlanAnswer {
  search_id: string;
  plan: ProspectPlan;
  regions_applied: boolean;
  query_applied?: boolean;
  total: number;
  sample: RefFirm[];
  estimate: ProspectEstimate;
  /** Search v2 What the planner did, in plain words. */
  steps?: string[];
}

export interface ProspectSearch {
  id: string;
  query: string;
  plan: ProspectPlan & { regions_applied?: boolean };
  estimate: Partial<ProspectEstimate>;
  status: "draft" | "estimated" | "approved" | "running" | "done" | "cancelled";
  budget_usd: number | null;
  created_by: string;
  decided_by: string | null;
  created_at: string;
}

export interface ProspectResult {
  ref_firm_id: string;
  origin: "central" | "live";
  decision: "pending" | "tick" | "cross";
  reason: string | null;
  organisation_id: string | null;
  /** "rules" when the search's rules (learnt from crosses) now leave this firm out. */
  excluded_by?: string | null;
  /** 2.38 When the firm was scored; null while it waits (its place in the list is Ogma's order until then). */
  ranked_at?: string | null;
}

export class ProspectError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

/** Search v2 A change to an earlier plan ("change this search"): the owner's words and the plan they change. */
export interface Refine {
  feedback: string;
  previous_plan: ProspectPlan;
}

/** The plan as the planner reads it back: what it searched for, without its recorded steps or rank details. */
function previousPlan(plan: ProspectPlan): Record<string, unknown> {
  const { steps: _steps, rank, copied_from: _copied, ...rest } = plan;
  return { ...rest, criteria: (rank?.criteria ?? []).filter((c) => c.key !== "is_niche").map(({ key, label, kind }) => ({ key, label, kind })) };
}

/** 2.16, 2.17: plan the search, search the database and record it (the prospects Edge Function). With `onStep`, the
 *  planner's steps arrive as they happen (one JSON object a line) and each is passed on. */
export async function planProspects(opts: { token: string; query: string; plan?: PlanInput; refine?: Refine; onStep?: (step: string) => void;
                                            url?: string; anonKey?: string; fetchImpl?: typeof fetch }): Promise<PlanAnswer> {
  const f = opts.fetchImpl ?? httpFetch;
  const body: Record<string, unknown> = { query: opts.query };
  if (opts.plan) body.plan = opts.plan;
  if (opts.refine) Object.assign(body, { feedback: opts.refine.feedback, previous_plan: previousPlan(opts.refine.previous_plan) });
  if (opts.onStep) body.stream = true;
  let res: Response;
  try {
    res = await f(opts.url ?? functionUrl("prospects"), {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${opts.token}`, apikey: opts.anonKey ?? env.supabaseAnonKey },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ProspectError("Can't reach the server. Check your connection and try again.", 0);
  }
  if (res.status === 401) throw new ProspectError("Your session has ended. Sign in again.", 401);
  if (opts.onStep && res.ok && (res.headers.get("content-type") ?? "").includes("ndjson")) return readSteps(res, opts.onStep);
  const answer = (await res.json().catch(() => ({}))) as Partial<PlanAnswer> & { error?: string };
  if (res.status === 404 && !answer.error) throw new ProspectError("Finding prospects isn't switched on yet. Try again later, or ask us for help.", 404);
  if (!res.ok || !answer.search_id || !answer.plan || !answer.estimate) throw new ProspectError(answer.error ?? "Something went wrong. Try again in a moment.", res.status);
  answer.steps?.forEach((s) => opts.onStep?.(s));
  return answer as PlanAnswer;
}

/** The streamed reply: {"step": ...} lines as they happen, then {"result": ...} or {"error": ..., "status": ...}. */
async function readSteps(res: Response, onStep: (step: string) => void): Promise<PlanAnswer> {
  const fail = () => new ProspectError("Something went wrong. Try again in a moment.", 500);
  if (!res.body) throw fail();
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (value) buf += dec.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf("\n")) >= 0) {
      const line = buf.slice(0, i).trim();
      buf = buf.slice(i + 1);
      if (!line) continue;
      let o: { step?: unknown; result?: PlanAnswer; error?: unknown; status?: unknown };
      try {
        o = JSON.parse(line);
      } catch {
        continue;
      }
      if (typeof o.step === "string") onStep(o.step);
      else if (o.result?.search_id && o.result.plan && o.result.estimate) return o.result;
      else if (typeof o.error === "string") throw new ProspectError(o.error, Number(o.status) || 500);
    }
    if (done) throw fail();
  }
}

/** "Wedding planners · GB · Bath"; "Any trade · GB · 2–49 employees · owner-led" */
export function planLine(plan: ProspectPlan, labels: Map<string, string>): string {
  const trade = plan.niches.length ? plan.niches.map((n) => labels.get(n) ?? n).join(", ") : plan.facts ? "Any trade" : "";
  return [trade, plan.countries.join(", "), plan.regions.join(", "), ...factChips(plan.facts).map((c) => c.label.toLowerCase())]
    .filter(Boolean).join(" · ");
}

const pounds = (n: number) => n >= 1_000_000 ? `£${+(n / 1_000_000).toFixed(1)}m` : n >= 1000 ? `£${Math.round(n / 1000)}k` : `£${n}`;

function rangeLabel(r: FactRange, unit: (n: number) => string, noun: string): string {
  if (r.min !== undefined && r.max !== undefined) return `${unit(r.min)}–${unit(r.max)} ${noun}`;
  if (r.min !== undefined) return `${unit(r.min)}+ ${noun}`;
  return `Up to ${unit(r.max ?? 0)} ${noun}`;
}

/** #540 Each firm fact in a plan as a chip's words, in the plan's order. */
export function factChips(facts: FirmFacts | undefined): { key: FactKey; label: string }[] {
  if (!facts) return [];
  const out: { key: FactKey; label: string }[] = [];
  if (facts.employees) out.push({ key: "employees", label: rangeLabel(facts.employees, String, "employees") });
  if (facts.turnover) out.push({ key: "turnover", label: rangeLabel(facts.turnover, pounds, "turnover") });
  if (facts.owner_led) out.push({ key: "owner_led", label: "Owner-led" });
  if (facts.independent) out.push({ key: "independent", label: "Independent" });
  if (facts.growth?.length) out.push({ key: "growth", label: facts.growth.length > 1 ? "Steady or rising" : facts.growth[0] === "rising" ? "Rising" : "Steady" });
  if (facts.trading_years) out.push({ key: "trading_years", label: `Trading ${facts.trading_years}+ years` });
  return out;
}

const CRITERION_WORDS: Record<string, string> = {
  niches: "the trade", regions: "the region", query: "the specialism", words: "the specialism", labels: "the labels",
  size_bands: "the size", customer_types: "who they sell to", web: "the online presence", sales_team: "the sales team",
  clients: "the clients",
};

/** #540 A criterion as the "dropping it gives" line names it: a fact chip's words, else the trade or the place. */
export function criterionLabel(key: string, plan: ProspectPlan): string {
  const chip = factChips(plan.facts).find((c) => c.key === key);
  if (chip) return chip.label.toLowerCase();
  return CRITERION_WORDS[key] ?? key.replace(/_/g, " ");
}

/** #540 The plan as the prospects function takes it back, with a change: the firm facts set or removed (undefined
 *  removes one), the trade, or the regions. The criteria carry over, so the new search ranks the same way. */
export function planInput(plan: ProspectPlan, change: { facts?: FirmFacts; niches?: string[]; regions?: string[];
                                                        customer_types?: string[]; size_bands?: string[]; need?: Facet[];
                                                        web?: null; sales_team?: null; words?: string[]; labels?: string[];
                                                        clients?: null }): PlanInput {
  const facts: FirmFacts = { ...plan.facts, ...change.facts };
  for (const k of Object.keys(change.facts ?? {}) as FactKey[]) if (change.facts?.[k] === undefined) delete facts[k];
  const criteria = (plan.rank?.criteria ?? []).filter((c) => c.key !== "is_niche");
  return {
    title: plan.title, niches: change.niches ?? plan.niches, countries: plan.countries, regions: change.regions ?? plan.regions,
    terms: plan.terms, size_bands: change.size_bands ?? plan.size_bands, exclude_terms: plan.exclude_terms,
    ...((change.words ?? plan.words)?.length ? { words: change.words ?? plan.words }
        : !change.words && plan.query_text ? { query_text: plan.query_text } : {}),
    ...((change.labels ?? plan.labels)?.length ? { labels: change.labels ?? plan.labels,
                                                    ...(plan.labels_core ? { labels_core: true } : {}) } : {}),
    ...(plan.web && change.web !== null ? { web: plan.web } : {}),
    ...(plan.sales_team && change.sales_team !== null ? { sales_team: plan.sales_team } : {}),
    ...(plan.clients && change.clients !== null ? { clients: plan.clients } : {}),
    ...((change.need ?? plan.need) ? { need: change.need ?? plan.need } : {}),
    ...(plan.focus ? { focus: plan.focus } : {}),
    ...((change.customer_types ?? plan.customer_types)?.length ? { customer_types: change.customer_types ?? plan.customer_types } : {}),
    ...facts,
    ...(criteria.length ? { criteria, limit: plan.rank?.limit } : {}),
  };
}

export const usd = (n: number | null | undefined) => `$${Number(n ?? 0).toFixed(2)}`;

/** "Ask" can start the same flow: a request to find businesses rather than a question about the CRM. */
export function looksLikeProspecting(q: string): boolean {
  return /^\s*(please\s+)?(find|search for|look for|get me|build( me)? a list of|list)\s+(me\s+)?(new\s+)?(some\s+)?[a-z]/i.test(q)
    && /\b(in|across|around|near)\b/i.test(q);
}

const FACT_KEYS: readonly FactKey[] = ["employees", "turnover", "owner_led", "independent", "growth", "trading_years"];

/** #540 The plan with one criterion dropped (a firm fact, the trade or the regions); null when it can't be dropped. */
export function dropCriterion(plan: ProspectPlan, key: string): PlanInput | null {
  // Search v2: relaxing a need-to-have keeps it as a nice-to-have (it ranks), so nothing the owner asked for is lost
  if (plan.need) return plan.need.includes(key as Facet) ? planInput(plan, { need: plan.need.filter((k) => k !== key) }) : null;
  if ((FACT_KEYS as readonly string[]).includes(key)) return plan.facts?.[key as FactKey] === undefined ? null : planInput(plan, { facts: { [key]: undefined } });
  if (key === "niches") return plan.niches.length && plan.facts ? planInput(plan, { niches: [] }) : null;
  if (key === "regions") return plan.regions.length ? planInput(plan, { regions: [] }) : null;
  return null;
}
